import React, { useEffect } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import getCartPrice from '~/shared/utils/getCartPrice';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { useCart } from '~/techstyle-shared/react-cart';
import {
  Checkbox,
  mobile,
  ProductImage as BaseProductImage,
  ProductName as BaseProductName,
  useProductContext,
  useProductDetailContext,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import { usePrevious } from '../../utils/usePrevious';
import ProductDetailColorSelector from '../ProductDetailColorSelector';
import ProductDetailOtherSelectors from '../ProductDetailOtherSelectors';
import ProductDetailSizeSelector from '../ProductDetailSizeSelector';

import PromoPrice from './WearItWithPromoPrice';

const outfitImageBreakpoints = [{ minWidth: 0, pictureWidth: 100 }];

const ItemInformation = styled.div`
  display: flex;
`;

const ItemWrapper = styled.div`
  background: ${({ theme }) => theme.colors.palette.white};
  border: 1px solid ${({ theme }) => theme.colors.wearItWithItemBorder};
  padding: 16px;

  ${mobile`
    margin-bottom: 8px;
  `}
`;

const ItemHeading = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const ItemImage = styled(BaseProductImage)`
  margin-right: 12px;
  min-width: 80px;
  width: 80px;
`;

const ItemName = styled(BaseProductName)`
  color: ${({ theme }) => theme.colors.sublabelBlack};
  font-size: 18px;
  font-weight: 700;
  letter-spacing: ${-0.45 / 18}em;
`;

const StyledCheckbox = styled(Checkbox)``;

const SubscribeCheckboxLabel = styled.label`
  color: ${({ theme }) => theme.colors.textDefault};
  font-size: 16px;
  font-weight: 600;
  display: flex;
  line-height: 20px;
  align-items: center;

  ${StyledCheckbox} {
    margin-right: 6px;
  }
`;

const CheckboxFormField = styled.div`
  display: flex;
  position: relative;
`;

export default function WearItWithItem({
  isInAddToCartItems,
  isVip,
  promoCode,
  updateAddToCartDispatch,
}) {
  const product = useProductContext();
  const { networkStatus } = useCart({ isLoginRequired: true });
  const { isVipCart } = useCart({ isLoginRequired: true }) || {};
  const { selectionStateByProductIndex } = useProductDetailContext();
  const secondarySite = useSecondarySite();

  const currentSelection = selectionStateByProductIndex[0].selectedSKU;
  const previousCurrentSelection = usePrevious(currentSelection);
  const previousIsInAddToCartItems = usePrevious(isInAddToCartItems);

  useEffect(() => {
    if (
      previousCurrentSelection &&
      currentSelection &&
      previousCurrentSelection.productId !== currentSelection.productId
    ) {
      if (currentSelection.availableQuantity === 0) {
        updateAddToCartDispatch({ type: 'Remove', product: currentSelection });
      } else if (isInAddToCartItems) {
        updateAddToCartDispatch({ type: 'Add', product: currentSelection });
      }
    }
  }, [
    currentSelection,
    isInAddToCartItems,
    previousCurrentSelection,
    previousIsInAddToCartItems,
    updateAddToCartDispatch,
  ]);

  function updateCheckboxState() {
    if (!currentSelection || currentSelection.availableQuantity === 0) {
      return;
    }

    if (isInAddToCartItems) {
      updateAddToCartDispatch({ type: 'Remove', product: currentSelection });
    } else {
      updateAddToCartDispatch({ type: 'Add', product: currentSelection });
    }
  }

  const promo = product?.promos;

  if (!product || !promo || promo.promoCode !== promoCode) {
    return null;
  }

  const productPrice = getCartPrice({
    product,
    isVip,
    isVipCart,
    secondarySite,
  });

  return (
    <ItemWrapper
      data-cnstrc-item-id={product.masterProductId}
      data-cnstrc-item-name={product.label}
      data-cnstrc-item-price={productPrice}
    >
      <ItemHeading>
        <ItemName product={product} />
        <PromoPrice
          discountPrice={promo.promoPrice}
          regularPrice={product.defaultUnitPrice}
        />
      </ItemHeading>
      <ItemInformation>
        <ItemImage
          breakpoints={outfitImageBreakpoints}
          disableHover
          product={product}
        />
        <div>
          <ProductDetailColorSelector />
          <ProductDetailSizeSelector />
          <ProductDetailOtherSelectors />
          <CheckboxFormField>
            <StyledCheckbox
              id="add-to-cart"
              name="addWearItWithToCart"
              variant="purple"
              disabled={networkStatus.isLoading || !currentSelection}
              checked={isInAddToCartItems}
              onChange={updateCheckboxState}
              size="small"
              verticalAlign="flex-start"
              label={
                <SubscribeCheckboxLabel>
                  {isInAddToCartItems ? (
                    <FormattedMessage
                      id="site_pdp.ctl_added_to_outfit"
                      defaultMessage="Added to Outfit"
                    />
                  ) : (
                    <FormattedMessage
                      id="site_pdp.ctl_add_to_outfit"
                      defaultMessage="Add to Outfit"
                    />
                  )}
                </SubscribeCheckboxLabel>
              }
            />
          </CheckboxFormField>
        </div>
      </ItemInformation>
    </ItemWrapper>
  );
}

WearItWithItem.propTypes = {
  isInAddToCartItems: PropTypes.bool,
  isVip: PropTypes.bool,
  promoCode: PropTypes.string,
  updateAddToCartDispatch: PropTypes.func,
};
